import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  getPublicOffer,
  acceptPublicOffer,
} from "../../../api/public-offer/publicOffer.api";

import {
  getApiErrorMessage,
  isValidPublicOfferToken,
} from "../utils/publicOffer.utils";


const usePublicOffer = (
  token
) => {
  const [
    publicOffer,
    setPublicOffer,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    accepting,
    setAccepting,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState(null);

  const [
    acceptError,
    setAcceptError,
  ] = useState(null);


  /*
  |--------------------------------------------------------------------------
  | LOAD OFFER
  |--------------------------------------------------------------------------
  */

  const loadOffer =
    useCallback(
      async () => {

        if (
          !isValidPublicOfferToken(
            token
          )
        ) {
          setLoading(false);

          setError(
            "Invalid public offer link."
          );

          return;
        }

        try {

          setLoading(true);
          setError(null);

          const response =
            await getPublicOffer(
              token
            );


          /*
          |--------------------------------------------------------------------------
          | DEBUG PUBLIC OFFER RESPONSE
          |--------------------------------------------------------------------------
          */

          console.log(
            "PUBLIC OFFER RESPONSE:",
            response
          );

          console.log(
            "PUBLIC OFFER RESPONSE.DATA:",
            response?.data
          );


          /*
          |--------------------------------------------------------------------------
          | Backend response:
          |
          | {
          |   success: true,
          |   message: "...",
          |   data: {
          |     publicOffer: {...},
          |     offer: {...},
          |     client: {...},
          |     query: {...},
          |     items: [...]
          |   }
          | }
          |
          | Store the formatted public offer data.
          |--------------------------------------------------------------------------
          */

          setPublicOffer(
            response?.data || null
          );

        } catch (err) {

          setError(
            getApiErrorMessage(
              err,
              "Unable to load this offer."
            )
          );

        } finally {

          setLoading(false);

        }
      },
      [token]
    );


  /*
  |--------------------------------------------------------------------------
  | ACCEPT OFFER
  |--------------------------------------------------------------------------
  */

  const acceptOffer =
    useCallback(
      async () => {

        if (
          !isValidPublicOfferToken(
            token
          )
        ) {
          setAcceptError(
            "Invalid public offer link."
          );

          return null;
        }

        try {

          setAccepting(true);
          setAcceptError(null);

          const response =
            await acceptPublicOffer(
              token
            );


          /*
          |--------------------------------------------------------------------------
          | DEBUG ACCEPT RESPONSE
          |--------------------------------------------------------------------------
          */

          console.log(
            "PUBLIC OFFER ACCEPT RESPONSE:",
            response
          );

          console.log(
            "PUBLIC OFFER ACCEPT RESPONSE.DATA:",
            response?.data
          );


          /*
          |--------------------------------------------------------------------------
          | Backend response:
          |
          | {
          |   success: true,
          |   message: "...",
          |   data: formattedPublicOffer
          | }
          |
          | Store the formatted public offer data.
          |--------------------------------------------------------------------------
          */

          const data =
            response?.data || null;


          if (data) {
            setPublicOffer(
              data
            );
          }


          return data;

        } catch (err) {

          const message =
            getApiErrorMessage(
              err,
              "Unable to accept this offer."
            );

          setAcceptError(
            message
          );

          throw err;

        } finally {

          setAccepting(false);

        }
      },
      [token]
    );


  /*
  |--------------------------------------------------------------------------
  | INITIAL LOAD
  |--------------------------------------------------------------------------
  */

  useEffect(() => {
    loadOffer();
  }, [loadOffer]);


  return {
    publicOffer,

    loading,

    accepting,

    error,

    acceptError,

    loadOffer,

    acceptOffer,
  };
};


export default usePublicOffer;